import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle2, ShieldCheck, BadgeCheck, Globe2, Download } from "lucide-react";
import { PARTNER_OFFERINGS, PARTNERS, GCP_BROCHURES } from "../mock";
import BrochureDownloadModal from "../components/BrochureDownloadModal";

const BROCHURE_MAP = { "Google Cloud": GCP_BROCHURES };

export const partnerSlug = (name) =>
  name.toLowerCase().replace(/[()]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const PartnerDetailPage = () => {
  const { slug } = useParams();
  const partner = PARTNER_OFFERINGS.find((p) => partnerSlug(p.name) === slug);
  const [activeBrochure, setActiveBrochure] = React.useState(null);

  if (!partner) {
    return <Navigate to="/partnerofferings" replace />;
  }

  const logo = PARTNERS.find((p) => p.name === partner.logoName || p.name === partner.name)?.logo;
  const brochures = BROCHURE_MAP[partner.name];
  const brochureFor = (courseName) => brochures?.find((b) => b.name === courseName)?.file;

  return (
    <div className="bg-white min-h-screen">
      <Header/>

      <section className="relative overflow-hidden bg-gradient-to-br from-[#15348C] via-[#1E47A8] to-[#15348C] text-white">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-[#6BBE32]/10 rounded-full blur-3xl"/>
        <div className="relative max-w-7xl mx-auto px-4 py-16">
          <Reveal variant="left">
            <Link to="/partnerofferings" className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-white mb-6 transition-colors">
              <ArrowLeft size={15}/> All Partner Programs
            </Link>
          </Reveal>
          <Reveal variant="left" delay={80}>
            <div className="flex items-center gap-4 mb-5">
              <div className="w-16 h-16 rounded-xl bg-white flex items-center justify-center p-3 shadow-lg">
                {logo ? (
                  <img src={logo} alt={partner.name} className="max-h-10 max-w-full object-contain"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                ) : (
                  <span className="text-[#15348C] font-bold text-lg">{(partner.logoName || partner.name).slice(0,2).toUpperCase()}</span>
                )}
              </div>
              <div>
                <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-1">AUTHORISED TRAINING PARTNER</div>
                <h1 className="text-3xl lg:text-5xl font-bold leading-tight">{partner.name}</h1>
              </div>
            </div>
          </Reveal>
          <Reveal variant="left" delay={160}>
            <p className="mt-4 text-gray-300 text-lg max-w-2xl">
              Vendor-authorised curricula, official certification pathways and enterprise-grade delivery for {partner.name} programs.
            </p>
          </Reveal>
          <Reveal variant="left" delay={220}>
            <div className="mt-8">
              <Link to="/contact" className="sg-btn-glow inline-flex items-center gap-2 bg-[#6BBE32] hover:bg-[#4F9622] text-white font-semibold px-6 h-12 rounded-md shadow-lg shadow-[#6BBE32]/30 transition-colors">
                Request Program Brief <ArrowRight size={17}/>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-16">
        <Reveal>
          <div className="max-w-2xl mb-10">
            <div className="text-xs font-bold tracking-widest text-[#4F9622] mb-3">PROGRAMS &amp; CERTIFICATIONS</div>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">{partner.name} programs available</h2>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {partner.courses.map((c, i) => {
            const file = brochureFor(c);
            return (
              <Reveal key={c} variant="up" delay={i * 60}>
                <div className="sg-card group bg-white border border-gray-100 rounded-2xl p-6 h-full flex items-start gap-3">
                  <CheckCircle2 size={20} className="text-[#6BBE32] shrink-0 mt-0.5"/>
                  <div className="min-w-0">
                    <h3 className="font-bold text-[#15348C] group-hover:text-[#6BBE32] transition-colors">{c}</h3>
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                      {file && (
                        <button
                          type="button"
                          onClick={() => setActiveBrochure({ courseName: c, file })}
                          className="inline-flex items-center gap-1 text-sm font-semibold text-[#15348C] hover:text-[#6BBE32] sg-link"
                        >
                          Download Brochure <Download size={13}/>
                        </button>
                      )}
                      <Link to="/contact" className="inline-flex items-center gap-1 text-sm font-semibold text-[#4F9622] sg-link">
                        Request brief <ArrowRight size={13}/>
                      </Link>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="bg-[#F8F9FA] py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid sm:grid-cols-3 gap-5">
            <Reveal variant="up">
              <div className="bg-white border border-gray-100 rounded-2xl p-6">
                <ShieldCheck size={22} className="text-[#6BBE32] mb-3"/>
                <h4 className="font-bold text-[#15348C]">Governance &amp; Compliance</h4>
                <p className="text-sm text-gray-600 mt-2">Vendor-authorised curricula and audit-ready training records.</p>
              </div>
            </Reveal>
            <Reveal variant="up" delay={80}>
              <div className="bg-white border border-gray-100 rounded-2xl p-6">
                <BadgeCheck size={22} className="text-[#6BBE32] mb-3"/>
                <h4 className="font-bold text-[#15348C]">Certification Pathways</h4>
                <p className="text-sm text-gray-600 mt-2">Bundled exam vouchers and post-training mentor support.</p>
              </div>
            </Reveal>
            <Reveal variant="up" delay={160}>
              <div className="bg-white border border-gray-100 rounded-2xl p-6">
                <Globe2 size={22} className="text-[#6BBE32] mb-3"/>
                <h4 className="font-bold text-[#15348C]">Global Delivery</h4>
                <p className="text-sm text-gray-600 mt-2">Delivered across 60+ countries in 15+ languages.</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-20">
        <Reveal variant="scale">
          <div className="bg-[#15348C] rounded-3xl p-10 lg:p-14 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
            <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[#8BD835]/20 rounded-full blur-3xl"/>
            <div className="relative grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-3">GET STARTED</div>
                <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight">Bring {partner.name} certifications to your organisation.</h2>
                <p className="mt-3 text-gray-300">Talk to our advisors about volume pricing, delivery modalities and exam bundles.</p>
              </div>
              <div className="flex flex-wrap gap-3 lg:justify-end">
                <Link to="/contact" className="sg-btn-glow bg-[#8BD835] hover:bg-[#6BBE32] text-white font-semibold px-6 py-3 rounded-md transition-colors">Talk to Our Advisor</Link>
                <Link to="/partnerofferings" className="border-2 border-white/40 text-white font-semibold px-6 py-3 rounded-md hover:bg-white/10 hover:border-[#8BD835] transition-colors">View All Partners</Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <Footer/>

      {activeBrochure && (
        <BrochureDownloadModal
          courseName={activeBrochure.courseName}
          fileUrl={`/brochures/gcp/${encodeURIComponent(activeBrochure.file)}`}
          fileName={`${activeBrochure.courseName}.pdf`}
          source={`partner-page-${slug}`}
          onClose={() => setActiveBrochure(null)}
        />
      )}
    </div>
  );
};

export default PartnerDetailPage;
